import React, { useState, useEffect, useCallback } from 'react';
import {
  UserPlusIcon,
  PencilIcon,
  ShieldCheckIcon,
  TrashIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
  ExclamationCircleIcon,
  UsersIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import axiosInstance from '../utils/axios';
import toast from 'react-hot-toast';
import { PageHeader, StatCard, ConfirmDialog } from '../components/common';
import { motion } from 'framer-motion';
import { hasRole, ROLE_LABELS, ROLE_COLORS, roleColors } from '../config/permissions';

// Helper function to format relative time
const formatRelativeTime = (date) => {
  if (!date) return 'Never';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
};

// The system service account is created when the platform is set up and the
// cloud services sign in with it. It is not a person, so it cannot be edited
// or switched off from here.
const isProtectedAccount = (employee) => employee?.role === 'system';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentUser, setCurrentUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // create, edit, reset-password
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'employee',
    cellPhone: '',
    gender: '',
    dob: '',
  });

  // Fetch current user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axiosInstance.get('/employees/profile');
        setCurrentUser(response.data.employee);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfileError(error.response?.data?.message || error.message);
      }
    };
    fetchProfile();
  }, []);

  // Fetch all employees
  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/employees');
      setEmployees(response.data.employees || []);
    } catch (error) {
      if (error.response?.status === 403) {
        // Not an admin, just show empty state
        setEmployees([]);
      } else {
        toast.error('Failed to fetch employees');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Filter employees
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = 
      employee.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || employee.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (modalMode === 'create') {
        await axiosInstance.post('/employees/create', formData);
        toast.success('Employee created successfully');
      } else if (modalMode === 'edit') {
        const { password, ...updateData } = formData;
        await axiosInstance.put(`/employees/${selectedEmployee._id}`, updateData);
        toast.success('Employee updated successfully');
      } else if (modalMode === 'reset-password') {
        await axiosInstance.put(`/employees/${selectedEmployee._id}/reset-password`, {
          newPassword: formData.password
        });
        toast.success('Password reset successfully');
      }
      
      fetchEmployees();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  // Open modal
  const openModal = (mode, employee = null) => {
    setModalMode(mode);
    setSelectedEmployee(employee);
    
    if (mode === 'edit' && employee) {
      setFormData({
        ...employee,
        password: '',
        dob: employee.dob ? new Date(employee.dob).toISOString().split('T')[0] : ''
      });
    } else if (mode === 'reset-password') {
      setFormData({ ...formData, password: '' });
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'employee',
        cellPhone: '',
        gender: '',
        dob: '',
      });
    }
    setShowModal(true);
    setShowPassword(false);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedEmployee(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: 'employee',
      cellPhone: '',
      gender: '',
      dob: '',
    });
  };

  // Delete an employee outright. There is no deactivate - staff records live
  // in the CRM, and this site only needs the people who are still here.
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await axiosInstance.delete(`/employees/${deleteTarget._id || deleteTarget.id}`);
      toast.success('Employee deleted');
      setDeleteTarget(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete employee');
    } finally {
      setDeleting(false);
    }
  };

  // Check if current user is admin or developer
  const isAdmin = hasRole(currentUser?.role, 'admin');
  const isDeveloper = hasRole(currentUser?.role, 'developer');

  const isSelf = (employee) =>
    (employee._id || employee.id) === (currentUser?._id || currentUser?.id);

  // The route is already role-gated; don't misreport a failed or still-loading
  // profile fetch as a role refusal.
  if (profileError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <ExclamationCircleIcon className="mx-auto mb-4 h-12 w-12 text-red-600" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Couldn't load your profile</h3>
          <p className="text-gray-600">{profileError}</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <ExclamationCircleIcon className="mx-auto mb-4 h-12 w-12 text-yellow-600" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Restricted</h3>
          <p className="text-gray-600">
            Only administrators can access employee management features.
          </p>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const statistics = {
    total: employees.length,
    developers: employees.filter(e => e.role === 'developer').length,
    admins: employees.filter(e => e.role === 'admin').length,
    regular: employees.filter(e => e.role === 'employee').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        subtitle="Manage employee accounts and permissions"
        actions={
          <button
            onClick={() => openModal('create')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <UserPlusIcon className="h-5 w-5" />
            Add Employee
          </button>
        }
      />

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total"
          value={statistics.total}
          icon={UsersIcon}
          delay={0.1}
        />
        <StatCard
          label="Developers"
          value={statistics.developers}
          valueColor={ROLE_COLORS.developer.value}
          icon={ShieldCheckIcon}
          iconColor={ROLE_COLORS.developer.icon}
          delay={0.25}
        />
        <StatCard
          label="Admins"
          value={statistics.admins}
          valueColor={ROLE_COLORS.admin.value}
          icon={ShieldCheckIcon}
          iconColor={ROLE_COLORS.admin.icon}
          delay={0.3}
        />
        <StatCard
          label="Employees"
          value={statistics.regular}
          valueColor={ROLE_COLORS.employee.value}
          icon={UsersIcon}
          iconColor={ROLE_COLORS.employee.icon}
          delay={0.35}
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Role Filter */}
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              <option value="all">All Roles</option>
              <option value="developer">Developer</option>
              <option value="admin">Admin</option>
              <option value="employee">Employee</option>
            </select>
            <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          </div>

        </div>
      </div>

      {/* Employee List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredEmployees.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <UsersIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <p className="text-gray-600">No employees found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEmployees.map((employee) => (
                  <tr key={employee._id || employee.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-600 font-medium">
                            {employee.firstName?.charAt(0).toUpperCase()}
                            {employee.lastName?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {employee.firstName} {employee.lastName}
                          </div>
                          {employee.cellPhone && (
                            <div className="text-sm text-gray-500">{employee.cellPhone}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{employee.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColors(employee.role).badge}`}>
                        {hasRole(employee.role, 'admin') && <ShieldCheckIcon className="h-3 w-3" />}
                        {ROLE_LABELS[employee.role] || employee.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <span>{formatRelativeTime(employee.lastLogin)}</span>
                      </div>
                      {employee.loginCount > 0 && (
                        <div className="text-xs text-gray-400">{employee.loginCount} logins</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {isProtectedAccount(employee) ? (
                        <span className="text-gray-400 text-xs">Protected</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openModal('edit', employee)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openModal('reset-password', employee)}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Reset Password"
                          >
                            <KeyIcon className="h-4 w-4" />
                          </button>
                          {isAdmin && !isSelf(employee) && (
                            <button
                              onClick={() => setDeleteTarget(employee)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              {modalMode === 'create' && 'Add New Employee'}
              {modalMode === 'edit' && 'Edit Employee'}
              {modalMode === 'reset-password' && 'Reset Password'}
            </h2>

            {(
            <form onSubmit={handleSubmit}>
              {modalMode !== 'reset-password' ? (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {modalMode === 'create' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password *
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          required
                          minLength={8}
                          pattern="(?=.*[A-Z])(?=.*\d).*"
                          title="At least 8 characters with an uppercase letter and a number"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        At least 8 characters with an uppercase letter and a number
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <select
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="employee">Employee</option>
                        <option value="admin">Admin</option>
                        {isDeveloper && (
                          <option value="developer">Developer</option>
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.cellPhone}
                        onChange={(e) => setFormData({ ...formData, cellPhone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gender
                      </label>
                      <select
                        value={formData.gender}
                        onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        value={formData.dob}
                        onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                </>
              ) : (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Reset password for: <strong>{selectedEmployee?.email}</strong>
                  </p>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      minLength={8}
                      pattern="(?=.*[A-Z])(?=.*\d).*"
                      title="At least 8 characters with an uppercase letter and a number"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    At least 8 characters with an uppercase letter and a number
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {modalMode === 'create' && 'Create Employee'}
                  {modalMode === 'edit' && 'Update Employee'}
                  {modalMode === 'reset-password' && 'Reset Password'}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this employee?"
        message={
          <>
            <p>
              <strong>
                {deleteTarget?.firstName} {deleteTarget?.lastName}
              </strong>{' '}
              ({deleteTarget?.email}) will be permanently deleted, along with
              all of their account data.
            </p>
            <p>They will be signed out and will no longer be able to log in. This cannot be undone.</p>
          </>
        }
        confirmLabel="Delete Employee"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default Employees;
